/**
 * --------------------------------------------------------------------------
 * Theme Utilities
 * --------------------------------------------------------------------------
 * Attaches a shared utility object to the window to be used across
 * different component scripts.
 */

window.theme = window.theme || {};
window.theme.utils = window.theme.utils || {};

// Store original pushState/replaceState immediately before any intercepts can occur
window.theme.utils._originalPushState = history.pushState;
window.theme.utils._originalReplaceState = history.replaceState;

/**
 * A utility function to debounce function calls.
 * @param {Function} func The function to debounce.
 * @param {number} wait The delay in milliseconds.
 * @returns {Function} The debounced function.
 */
window.theme.utils.debounce = (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
};

/**
 * Rounds a price value based on the configured rounding logic.
 * @param {number} price - The price to round (in cents or decimal format).
 * @param {boolean} isInCents - Whether the price is already in cents (default: false).
 * @returns {number} The rounded price in the same format as input.
 * @example
 *   roundPrice(4.996) // returns 5.00 (up), 4.99 (down), or 5.00 (nearest)
 *   roundPrice(499.6, true) // returns 500 (up), 499 (down), or 500 (nearest) - in cents
 */
window.theme.utils.roundPrice = function(price, isInCents = false) {
  if (typeof price !== 'number' || isNaN(price)) return price;
  
  const roundingLogic = window.Shopify?.theme?.settings?.discount_rounding_logic || 'nearest';
  
  // Convert to cents if not already
  const priceInCents = isInCents ? price : Math.round(price * 100);
  
  // Apply rounding logic to 2 decimal places (cents level)
  let roundedCents;
  switch (roundingLogic) {
    case 'up':
      roundedCents = Math.ceil(priceInCents);
      break;
    case 'down':
      roundedCents = Math.floor(priceInCents);
      break;
    case 'nearest':
    default:
      roundedCents = Math.round(priceInCents);
      break;
  }
  
  // Return in the same format as input
  return isInCents ? roundedCents : roundedCents / 100;
};

/**
 * Handles a string to make it URL-friendly (identical to Shopify's handelize filter).
 * Converts to lowercase, replaces spaces and special characters with hyphens,
 * removes leading/trailing hyphens, and collapses multiple hyphens.
 * @param {string} str - The string to handelize.
 * @returns {string} The handelized string.
 * @example
 *   handelize('Hello World!') // returns 'hello-world'
 *   handelize('Product Name (2024)') // returns 'product-name-2024'
 */
window.theme.utils.handelize = function(str) {
  if (!str || typeof str !== 'string') return '';
  
  return str
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '') // Remove special characters except spaces and hyphens
    .replace(/[\s_-]+/g, '-') // Replace spaces, underscores, and multiple hyphens with single hyphen
    .replace(/^-+|-+$/g, ''); // Remove leading and trailing hyphens
};

/**
 * Product Info Cache Utility
 *
 * Usage:
 *   window.theme.utils.productInfoCache.prefetch(handle)
 *   window.theme.utils.productInfoCache.get(variantId)
 */
window.theme.utils.productInfoCache = {
  _cacheKey(variantId) {
    return `shopify-product-info:${variantId}`;
  },
  set(variantId, info) {
    try {
      localStorage.setItem(this._cacheKey(variantId), JSON.stringify(info));
    } catch (e) {}
  },
  get(variantId) {
    try {
      const data = localStorage.getItem(this._cacheKey(variantId));
      return data ? JSON.parse(data) : null;
    } catch (e) { return null; }
  },
  // Prefetch all variants for a product handle
  async prefetch(handle) {
    try {
      const res = await fetch(`/products/${handle}.js`);
      if (!res.ok) return;
      const product = await res.json();
      if (product && Array.isArray(product.variants)) {
        product.variants.forEach(variant => {
          this.set(variant.id, {
            ...variant,
            product_title: product.title,
            product_handle: product.handle,
            product_image: product.featured_image,
            product_url: product.url,
            product,
          });
        });
      }
    } catch (e) {}
  }
};

/**
 * Wire up all add-to-cart buttons to prefetch product info on mousedown.
 * Expects data-product-handle attribute on the button or form.
 */
window.theme.utils.enableProductPrefetch = function() {
  document.addEventListener('mousedown', function(e) {
    let el = e.target;
    // Traverse up to find a button or form with data-product-handle
    while (el && el !== document.body) {
      if (el.hasAttribute && el.hasAttribute('data-product-handle')) {
        const handle = el.getAttribute('data-product-handle');
        if (handle && window.theme.utils.productInfoCache) {
          window.theme.utils.productInfoCache.prefetch(handle);
        }
        break;
      }
      el = el.parentNode;
    }
  }, true);
};

window.theme.utils.enableProductPrefetch();

/**
 * Generate a Shopify CDN image URL with size transformations (mimics the image_url Liquid filter).
 * Handles Shopify's filename-based size parameters (e.g., _144x144, _medium, _large).
 * 
 * @param {string} src - The original Shopify CDN image URL.
 * @param {Object} options - Size options { width, height, size }.
 * @param {number} [options.width] - Desired width in pixels (e.g., 144).
 * @param {number} [options.height] - Desired height in pixels (e.g., 144).
 * @param {string} [options.size] - Named size (pico|icon|thumb|small|compact|medium|large|grande|1024x1024|2048x2048|master).
 * @returns {string} The transformed image URL with size parameter in filename.
 * 
 * @example
 *   imageUrl('product.jpg', { width: 144 }) // 'product_144x.jpg'
 *   imageUrl('product.jpg', { width: 144, height: 144 }) // 'product_144x144.jpg'
 *   imageUrl('product.jpg', { size: 'medium' }) // 'product_medium.jpg'
 *   imageUrl('https://cdn.shopify.com/.../product_large.jpg', { width: 144 }) // removes _large, adds _144x
 * 
 * @see https://shopify.dev/docs/api/liquid/filters/image_url
 */
window.theme.utils.imageUrl = function(src, options = {}) {
  if (!src || typeof src !== 'string') return src;
  if (!options || typeof options !== 'object') return src;
  
  // List of Shopify's named sizes and custom dimension patterns
  const sizePattern = /_(pico|icon|thumb|small|compact|medium|large|grande|original|1024x1024|2048x2048|master|\d+x\d*)\.(jpg|jpeg|gif|png|webp)/gi;
  
  // Determine the size parameter to add
  let sizeParam = '';
  
  if (options.size && typeof options.size === 'string') {
    // Use named size (e.g., 'medium', 'large')
    sizeParam = options.size;
  } else if (options.width || options.height) {
    // Build custom dimensions (e.g., '144x144', '144x', 'x144')
    const w = options.width ? parseInt(options.width) : '';
    const h = options.height ? parseInt(options.height) : '';
    sizeParam = `${w}x${h}`;
  }
  
  // If no size specified, return original
  if (!sizeParam) return src;
  
  // Remove any existing size parameters from the filename
  let cleanSrc = src.replace(sizePattern, '.$2');
  
  // Add the new size parameter before the file extension
  const imageUrl = cleanSrc.replace(/\.(jpg|jpeg|gif|png|webp)$/i, `_${sizeParam}.$1`);
  
  return imageUrl;
};

window.theme.utils.money = function(amount) {
  return new Intl.NumberFormat("en-GB", { style: "currency", currency: window.Shopify.currency.active }).format(parseFloat(amount));
};

/**
 * Button Disabled State Watcher
 * 
 * Watches buttons with data-disabled-text attributes and automatically toggles
 * between their original content and disabled text based on their disabled state.
 * Uses MutationObserver for live updates.
 */
window.theme.utils.buttonDisabledWatcher = {
  // Store original button content to restore when re-enabled
  originalContents: new WeakMap(),
  
  // Initialize the watcher for all existing buttons
  init() {
    this.watchExistingButtons();
    this.watchNewButtons();
  },
  
  // Watch all existing buttons with data-disabled-text
  watchExistingButtons() {
    const buttons = document.querySelectorAll('button[data-disabled-text]');
    buttons.forEach(button => this.setupButton(button));
  },
  
  // Watch for new buttons being added to the DOM
  watchNewButtons() {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          // Check if the added node is a button with data-disabled-text
          if (node.nodeType === Node.ELEMENT_NODE) {
            if (node.matches && node.matches('button[data-disabled-text]')) {
              this.setupButton(node);
            }
            // Also check child buttons
            const childButtons = node.querySelectorAll ? node.querySelectorAll('button[data-disabled-text]') : [];
            childButtons.forEach(button => this.setupButton(button));
          }
        });
      });
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  },
  
  // Set up a single button for disabled state watching
  setupButton(button) {
    // Store original content if not already stored
    if (!this.originalContents.has(button)) {
      this.originalContents.set(button, button.innerHTML);
    }
    
    // Set initial state
    this.updateButtonContent(button);
    
    // Watch for disabled attribute changes
    const buttonObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'disabled') {
          this.updateButtonContent(button);
        }
      });
    });
    
    buttonObserver.observe(button, {
      attributes: true,
      attributeFilter: ['disabled', 'data-out-of-stock']
    });
  },
  
  // Update button content based on disabled state
  updateButtonContent(button) {
    const disabledText = button.getAttribute('data-disabled-text');
    const originalContent = this.originalContents.get(button);
    
    if (!disabledText || !originalContent) return;
    
    if (button.disabled || button.hasAttribute('data-out-of-stock')) {
      button.innerHTML = disabledText;
    } else {
      button.innerHTML = originalContent;
    }
  },
  
  // Manually update a specific button (useful for programmatic state changes)
  updateButton(button) {
    if (button && button.dataset.disabledText) {
      this.updateButtonContent(button);
    }
  },
  
  // Update all buttons (useful for bulk state changes)
  updateAllButtons() {
    const buttons = document.querySelectorAll('button[data-disabled-text]');
    buttons.forEach(button => this.updateButtonContent(button));
  }
};

/**
 * Product Form Variant Watcher
 * 
 * Automatically updates quantity input max values when variant selection changes
 * in product forms. Watches for changes to select elements and updates any
 * quantity inputs within the same form. Also handles button disabled states
 * when products are out of stock.
 */
window.theme.utils.productFormVariantWatcher = {
  // Initialize the variant watcher for all existing product forms
  init() {
    this.watchExistingForms();
    this.watchNewForms();
  },
  
  // Watch all existing product forms
  watchExistingForms() {
    const forms = document.querySelectorAll('form[action*="/cart/add"]');
    forms.forEach(form => this.setupForm(form));
  },
  
  // Watch for new product forms being added to the DOM
  watchNewForms() {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          // Check if the added node is a form with cart add action
          if (node.nodeType === Node.ELEMENT_NODE) {
            if (node.matches && node.matches('form[action*="/cart/add"]')) {
              this.setupForm(node);
            }
            // Also check child forms
            const childForms = node.querySelectorAll ? node.querySelectorAll('form[action*="/cart/add"]') : [];
            childForms.forEach(form => this.setupForm(form));
          }
        });
      });
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  },
  
  // Set up a single product form for variant watching
  // 
  // ARCHITECTURE NOTE: There are TWO systems for managing button states:
  // 1. component-product-options.js (PRIMARY) - Used when <product-options> element exists
  //    - Better button finding logic (searches outside form DOM)
  //    - Handles related variants, quantity pickers, etc.
  //    - Used on product pages AND collection pages (product cards)
  // 2. utils.js updateButtonStates() (FALLBACK) - Only for forms WITHOUT product-options
  //    - Simpler logic, only for basic product forms
  // 
  // When product-options exists, it is the SINGLE SOURCE OF TRUTH.
  // This function should NEVER manage button states when product-options is present.
  setupForm(form) {
    const variantSelect = form.querySelector('select[name="id"]');
    if (!variantSelect) return;
    
    // Check if this is a quick-add form (these should use FALLBACK MODE even if inside product-options)
    const isQuickAddForm = form.closest('[data-quick-add-forms="true"]') !== null || 
                          (form.id && typeof form.id === 'string' && form.id.startsWith('quick-add-form-'));
    
    // Check if product-options component is managing this form
    // Search in multiple ways to catch all cases (product pages, collection pages, etc.)
    // BUT: Quick-add forms should always use FALLBACK MODE, even if inside product-options
    const productOptions = isQuickAddForm ? null : (
      form.closest('product-options') || 
      (form.id && document.querySelector(`product-options[data-form-id="${form.id}"]`)) ||
      (form.id && Array.from(document.querySelectorAll('product-options')).find(el => {
        const formId = el.dataset.formId || el.querySelector(`form#${form.id}`) ? form.id : null;
        return formId === form.id;
      }))
    );
    
    // If product-options exists, skip button state management (it handles it better)
    // But still update quantity max values (product-options doesn't handle this)
    if (productOptions) {
      // Only update quantity max, not button states
      this.updateQuantityMax(form);
      
      // Still watch for variant changes to update quantity max
      variantSelect.addEventListener('change', () => {
        this.updateQuantityMax(form);
      });
      return;
    }
    
    // No product-options component, so we handle everything (FALLBACK MODE)
    // Set initial max quantity and button states
    this.updateFormState(form);
    
    // Watch for variant selection changes
    variantSelect.addEventListener('change', () => {
      this.updateFormState(form);
    });
  },
  
  // Update both quantity max values and button states for a form
  updateFormState(form) {
    this.updateQuantityMax(form);
    this.updateButtonStates(form);
  },
  
  // Update the max quantity for all quantity inputs in a form
  updateQuantityMax(form) {
    const variantSelect = form.querySelector('select[name="id"]');
    const quantityInputs = form.querySelectorAll('input[name="quantity"][type="number"]');
    
    if (!variantSelect || quantityInputs.length === 0) return;
    
    const selectedOption = variantSelect.querySelector('option:checked');
    if (!selectedOption) return;
    
    const maxQuantity = selectedOption.getAttribute('data-max-quantity');
    if (!maxQuantity) return;
    
    const maxValue = parseInt(maxQuantity, 10);
    if (isNaN(maxValue)) return;
    
    // Update all quantity inputs in the form
    quantityInputs.forEach(input => {
      const currentMax = parseInt(input.getAttribute('max'));
      if (currentMax !== maxValue) {
        input.setAttribute('max', maxValue);
        
        // If current value exceeds new max, adjust it
        const currentValue = parseInt(input.value);
        if (currentValue > maxValue) {
          input.value = maxValue;
          // Trigger change event for any listeners
          input.dispatchEvent(new Event('change', { bubbles: true }));
        } else if (currentValue < 1) {
          input.value = 1;
          // Trigger change event for any listeners
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    });
    
    // Also update any cart-quantity-input components within the form
    const cartQuantityInputs = form.querySelectorAll('cart-quantity-input');
    cartQuantityInputs.forEach(component => {
      if (component.updateMaxQuantity) {
        component.updateMaxQuantity(maxValue);
      }
    });
  },
  
  // Update button states based on variant availability
  // NOTE: This is ONLY used for forms WITHOUT product-options component.
  // Forms with product-options component use component-product-options.js instead.
  // 
  // CONSOLIDATION: When product-options exists, it is the SINGLE SOURCE OF TRUTH for button states.
  // This function should NEVER run when product-options is present to avoid conflicts.
  updateButtonStates(form) {
    // Check if this is a quick-add form (these should use FALLBACK MODE even if inside product-options)
    const isQuickAddForm = form.closest('[data-quick-add-forms="true"]') !== null || 
                          (form.id && typeof form.id === 'string' && form.id.startsWith('quick-add-form-'));
    
    // Check if product-options component is managing this form
    // Search in multiple ways to ensure we catch all cases (collection pages, product pages, etc.)
    // BUT: Quick-add forms should always use FALLBACK MODE, even if inside product-options
    const productOptions = isQuickAddForm ? null : (
      form.closest('product-options') || 
      (form.id && document.querySelector(`product-options[data-form-id="${form.id}"]`)) ||
      (form.id && Array.from(document.querySelectorAll('product-options')).find(el => {
        const formId = el.dataset.formId || el.querySelector(`form#${form.id}`) ? form.id : null;
        return formId === form.id;
      }))
    );
    
    if (productOptions) {
      return;
    }
    
    const variantSelect = form.querySelector('select[name="id"]');
    
    // Get form ID for for attribute detection
    const formId = form.id;
    
    // Find buttons within the form
    const buttonsInsideForm = form.querySelectorAll('button[type="submit"], button[data-submit-closest-product-form="true"]');
    
    // Find buttons outside the form with for attribute matching form ID
    const buttonsWithFor = formId ? document.querySelectorAll(`button[for="${formId}"], button[data-submit-product-form="${formId}"]`) : [];
    
    // Combine both button arrays
    const buttons = [...buttonsInsideForm, ...buttonsWithFor];
    
    if (!variantSelect || buttons.length === 0) return;
    
    const selectedOption = variantSelect.querySelector('option:checked');
    if (!selectedOption) return;
    
    // Use getAttribute to ensure we get the raw string value (handles negative numbers correctly)
    const maxQuantity = selectedOption.getAttribute('data-max-quantity') || selectedOption.dataset.maxQuantity;
    if (!maxQuantity) return;
    
    const maxValue = parseInt(maxQuantity, 10);
    if (isNaN(maxValue)) return;
    
    // Get inventory policy to check if backorders are denied
    const inventoryPolicy = selectedOption.getAttribute('data-inventory-policy') || selectedOption.dataset.inventoryPolicy || null;
    const deniesBackorders = inventoryPolicy === 'deny';
    
    // Check if variant is out of stock AND inventory policy denies selling when out of stock
    // This matches the logic in component-product-options.js
    const isOutOfStock = maxValue <= 0;
    const shouldDisable = isOutOfStock && deniesBackorders;
    
    // Update all submit buttons in the form
    buttons.forEach((button) => {
      if (shouldDisable) {
        button.disabled = true;
        button.setAttribute('data-out-of-stock', 'true');
        button.classList.add('opacity-50', 'cursor-not-allowed');
      } else {
        button.disabled = false;
        button.removeAttribute('data-out-of-stock');
        button.classList.remove('opacity-50', 'cursor-not-allowed');
      }
      
      // The MutationObserver in buttonDisabledWatcher will automatically handle button content updates
      // when the disabled attribute changes, so no manual call is needed here
    });
  },
  
  // Manually update a specific form (useful for programmatic variant changes)
  updateForm(form) {
    if (form && form.action && form.action.includes('/cart/add')) {
      this.updateFormState(form);
    }
  },
  
  // Update all forms (useful for bulk updates)
  updateAllForms() {
    const forms = document.querySelectorAll('form[action*="/cart/add"]');
    forms.forEach(form => this.updateFormState(form));
  }
};

/**
 * URL Change Event System
 * Standardized event system for notifying components when the URL changes.
 * Uses a single generic event with type-based details for extensibility.
 * Automatically handles history.pushState when a new URL is provided.
 * 
 * @param {string} type - The type of URL change (e.g., 'collection-filter', 'browser-navigation')
 * @param {Object} data - Additional data to include in the event detail
 * @param {string} [data.url] - Optional new URL to navigate to. If provided, performs pushState before dispatching event.
 * @param {Object} [data.state] - Optional state object for pushState
 * 
 * @example
 *   // Dispatch a URL change event with pushState
 *   window.theme.utils.dispatchUrlChange('collection-filter', { 
 *     url: newUrl.toString(),
 *     urlParameters: 'filter.p.m.custom.category=Boxer+Briefs'
 *   });
 * 
 * @example
 *   // Dispatch a URL change event without changing URL (just notify)
 *   window.theme.utils.dispatchUrlChange('collection-filter', { cleared: true });
 * 
 * @example
 *   // Listen for URL change events
 *   document.addEventListener('url-change', (e) => {
 *     if (e.detail.type === 'collection-filter') {
 *       // Handle collection filter URL change
 *     }
 *   });
 */
window.theme.utils.dispatchUrlChange = function(type, data = {}) {
  let url = window.location.href;
  let state = null;
  const historyAction = typeof data.historyAction === 'string' ? data.historyAction.toLowerCase() : 'push';
  const useReplaceState = historyAction === 'replace';
  
  // If a new URL is provided, perform pushState/replaceState
  if (data.url && typeof data.url === 'string') {
    url = data.url;
    state = data.state || { urlParameters: data.urlParameters || '' };
    
    const historyMethod = useReplaceState
      ? window.theme.utils._originalReplaceState || history.replaceState
      : window.theme.utils._originalPushState || history.pushState;
    
    // Use stored original methods to bypass intercepts and prevent duplicate event dispatches
    // We dispatch the event ourselves, so we don't want the intercept to also dispatch
    historyMethod.call(history, state, '', url);
  }
  
  // Dispatch standardized event on document
  document.dispatchEvent(new CustomEvent('url-change', {
    detail: {
      type: type,
      url: url,
      data: data
    },
    bubbles: true
  }));
  
  // Also dispatch on window for global listeners
  window.dispatchEvent(new CustomEvent('url-change', {
    detail: {
      type: type,
      url: url,
      data: data
    },
    bubbles: true
  }));
};

// Initialize both watchers when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.theme.utils.buttonDisabledWatcher.init();
    window.theme.utils.productFormVariantWatcher.init();
    // Initialize offscreen popover observer
    if (window.theme.utils.offscreenPopoverObserver) {
      window.theme.utils.offscreenPopoverObserver.init();
    }
  });
} else {
  window.theme.utils.buttonDisabledWatcher.init();
  window.theme.utils.productFormVariantWatcher.init();
  // Initialize offscreen popover observer
  if (window.theme.utils.offscreenPopoverObserver) {
    window.theme.utils.offscreenPopoverObserver.init();
  }
}

/**
 * Offscreen Popover Observer
 * 
 * Observes elements with the attribute:
 *   data-popover-when-offscreen="[popover-id]"
 * When the observed element is NOT visible in the viewport, the specified popover
 * is shown. When it becomes visible again, the popover is hidden.
 */
window.theme.utils.offscreenPopoverObserver = (function() {
  const SELECTOR = '[data-popover-when-offscreen]';
  const observed = new WeakMap();
  const suppression = new WeakMap(); // Map<HTMLElement target, { suppressedUntilVisible: boolean }>
  const targetListeners = new WeakMap(); // Map<HTMLElement target, Function>
  const pendingActions = new WeakMap(); // Map<HTMLElement, { timeout: number, action: Function }>
  let io = null;
  let initialized = false;
  const DEBOUNCE_DELAY = 100; // Delay in ms to allow layout shifts to settle

  function normalizeTargetId(raw) {
    if (!raw) return '';
    const id = raw.trim();
    return id.startsWith('#') ? id.slice(1) : id;
  }

  function getTargetElement(fromEl) {
    const raw = fromEl.getAttribute('data-popover-when-offscreen');
    const id = normalizeTargetId(raw);
    if (!id) return null;
    return document.getElementById(id);
  }

  function getSuppression(target) {
    let state = suppression.get(target);
    if (!state) { state = { suppressedUntilVisible: false }; suppression.set(target, state); }
    return state;
  }

  function showPopover(target) {
    try {
      if (!target) {
        console.warn('[offscreenPopoverObserver] showPopover: target is null or undefined');
        return;
      }
      
      if (target.hasAttribute('popover') && typeof target.showPopover === 'function') {
        if (!target.matches(':popover-open')) {
          target.showPopover();
        }
      } else if (target.tagName.toLowerCase() === 'dialog' && typeof target.showModal === 'function') {
        if (!target.open) {
          target.showModal();
        }
      } else if (target.hasAttribute('popover')) {
          target.toggleAttribute('open', true);
      } else {
        target.classList.remove('opacity-0', 'pointer-events-none');
      }
    } catch (e) {
      console.error('[offscreenPopoverObserver] showPopover: Error showing popover', e);
    }
  }

  function hidePopover(target) {
    // try {
      if (!target) {
        console.warn('[offscreenPopoverObserver] hidePopover: target is null or undefined');
        return;
      }
      // Check if this is a popover element inside a dialog-popover component
      // The popover is a direct child of dialog-popover, so check parent first
      let dialogPopoverComponent = null;
      if (target.hasAttribute('popover')) {
        // Check parent first (most common case)
        const parent = target.parentElement;
        if (parent && parent.tagName === 'DIALOG-POPOVER') {
          dialogPopoverComponent = parent;
        } else {
          // Fallback to closest ancestor
          dialogPopoverComponent = target.closest('dialog-popover');
        }
      } else if (target.tagName === 'DIALOG-POPOVER') {
        // Target is already the dialog-popover component
        dialogPopoverComponent = target;
      } else {
        // Try to find dialog-popover component as ancestor
        dialogPopoverComponent = target.closest('dialog-popover');
        if (!dialogPopoverComponent) {
          dialogPopoverComponent = target;
        }
      }
      
      if (dialogPopoverComponent && typeof dialogPopoverComponent.closeDialog === 'function') {
        // CRITICAL: When trigger element comes back into view, we MUST close the popover
        // regardless of prevent-close. Use a special flag to force the close.
        
        // Set a special attribute to indicate this is an offscreen observer close
        // This will be checked in beforetoggle to allow the close even with prevent-close
        if (dialogPopoverComponent.dialog) {
          dialogPopoverComponent.dialog.setAttribute('data-offscreen-observer-close', 'true');
        }
        
        // Use the component's closeDialog method which handles programmatic closes
        // Pass true to force close even with prevent-close enabled
        if (typeof dialogPopoverComponent.closeDialog === 'function') {
          // Check if closeDialog accepts a force parameter
          if (dialogPopoverComponent.closeDialog.length > 0) {
            dialogPopoverComponent.closeDialog(true); // Force close
          } else {
            dialogPopoverComponent.closeDialog(); // Regular close (will use the attribute we set)
          }
        }
        
        // Clear the offscreen observer flag after a delay
        Promise.resolve().then(() => {
          if (dialogPopoverComponent?.dialog?.hasAttribute('data-offscreen-observer-close')) {
            dialogPopoverComponent.dialog.removeAttribute('data-offscreen-observer-close');
          }
        });
        
        return;
      } else {
        target.classList.add('opacity-0', 'pointer-events-none', 'transition-opacity', 'duration-200');
      }
      
      // Fallback: Mark as programmatic close before calling hidePopover
      // This ensures prevent-close won't block it
      target.setAttribute('data-programmatic-close', 'true');
      target.setAttribute('data-offscreen-observer-close', 'true');
      
      // If target is a dialog-popover component, get the actual popover element
      let dialogElement = target;
      if (target.tagName === 'DIALOG-POPOVER') {
        dialogElement = target.querySelector('[popover]');
      }
      
      if (dialogElement) {
        if (typeof dialogElement.hidePopover === 'function') {
          if (dialogElement.matches(':popover-open')) {
            dialogElement.hidePopover();
          }
        } else if (dialogElement.tagName.toLowerCase() === 'dialog' && typeof dialogElement.close === 'function') {
          if (dialogElement.open) dialogElement.close();
        } else {
          if (dialogElement.hasAttribute('popover')) dialogElement.toggleAttribute('open', false);
        }
      } else {
        // Fallback for non-dialog elements
        if (typeof target.hidePopover === 'function') {
          if (target.matches(':popover-open')) {
            target.hidePopover();
          }
        } else if (target.hasAttribute('popover')) {
          target.toggleAttribute('open', false);
        }
      }
      
      // Clear the programmatic flag after a short delay
      Promise.resolve().then(() => {
        if (target.hasAttribute('data-programmatic-close')) {
          target.removeAttribute('data-programmatic-close');
        }
        if (target.hasAttribute('data-offscreen-observer-close')) {
          target.removeAttribute('data-offscreen-observer-close');
        }
        if (dialogElement && dialogElement !== target && dialogElement.hasAttribute('data-programmatic-close')) {
          dialogElement.removeAttribute('data-programmatic-close');
        }
        if (dialogElement && dialogElement !== target && dialogElement.hasAttribute('data-offscreen-observer-close')) {
          dialogElement.removeAttribute('data-offscreen-observer-close');
        }
      });
    try {
    } catch (e) {
      console.error('[offscreenPopoverObserver] Error hiding popover', e);
    }
  }

  function onIntersect(entries) {
    entries.forEach(entry => {
      const el = entry.target;
      const state = observed.get(el) || { visible: null };
      const nowVisible = entry.isIntersecting;
      
      // Check if scroll position has passed the element
      // Only trigger if element is above viewport (scrolled past), not below (not yet scrolled to)
      const rect = entry.boundingClientRect;
      const hasScrolledPast = rect.top < 0; // Element is above viewport
      const isBelowViewport = rect.top > (window.innerHeight || document.documentElement.clientHeight); // Element is below viewport
      
      // If element is not visible and we haven't scrolled past it yet, don't trigger
      if (!nowVisible && !hasScrolledPast) {
        return; // Element is below viewport, not yet scrolled to
      }
      
      if (state.visible === nowVisible) {
        return; // no change
      }
      
      state.visible = nowVisible;
      observed.set(el, state);

      const target = getTargetElement(el);
      if (!target) {
        console.warn('[offscreenPopoverObserver] onIntersect: No target element found for', el);
        return;
      }

      // Clear any pending action for this element
      const pending = pendingActions.get(el);
      if (pending && pending.timeout) {
        clearTimeout(pending.timeout);
        pendingActions.delete(el);
      }

      // Debounce the action to allow layout shifts to settle
      const action = () => {
      if (nowVisible) {
        // Element is visible again: always clear suppression and hide if open
        // Suppression should only prevent opening, not closing
        // CRITICAL: When trigger comes back into view, we MUST close the popover
        // regardless of prevent-close setting
        const sup = getSuppression(target);
        sup.suppressedUntilVisible = false;
        
        // Force close the popover when element becomes visible
        // This MUST happen even if prevent-close is enabled
        hidePopover(target);
      } else {
        // Element went offscreen: only show if not suppressed due to a manual close while offscreen
        // AND we've scrolled past it (it's above the viewport)
        if (hasScrolledPast) {
          const sup = getSuppression(target);
          if (!sup.suppressedUntilVisible) {
            showPopover(target);
          }
        }
      }
        pendingActions.delete(el);
      };

      // Use requestAnimationFrame + setTimeout to allow layout shifts to settle
      // This prevents the observer from triggering during layout shifts caused by popover opening
      const timeout = setTimeout(() => {
        requestAnimationFrame(() => {
          // Double-check visibility state hasn't changed during the delay
          const currentState = observed.get(el);
          if (currentState && currentState.visible === nowVisible) {
            action();
          } else {
            pendingActions.delete(el);
          }
        });
      }, DEBOUNCE_DELAY);

      pendingActions.set(el, { timeout, action });
    });
  }

  function ensureObserver() {
    if (io) return io;
    io = new IntersectionObserver(onIntersect, { threshold: 0.01, rootMargin: '0px' });
    return io;
  }

  function observeElement(el) {
    if (!el || observed.has(el)) return;
    const target = getTargetElement(el);
    if (!target) return;
    ensureObserver().observe(el);
    observed.set(el, { visible: null });

    // Attach a single toggle listener to the target to detect manual close while offscreen
    if (!targetListeners.get(target)) {
      const handler = (ev) => {
        try {
          // Prefer ToggleEvent.newState when available
          // OR check class method opacity-0 and pointer-events-none
          let newState = 'closed';
          if (target.matches('[popover]') || target.tagName === 'DIALOG-POPOVER') {
            newState = ev && ev.newState ? ev.newState : (target.matches(':popover-open') ? 'open' : 'closed');
          } else {
            newState = target.classList.contains('opacity-0') && target.classList.contains('pointer-events-none') ? 'closed' : 'open';
          }

          console.log('observeElement', el, target, newState);

          if (newState === 'closed') {
            // Find any observed element referencing this target and check its visibility state
            // If any is currently not visible, suppress until visible again
            let anyOffscreen = false;
            observed.forEach((st, observedEl) => {
              if (getTargetElement(observedEl) === target && st.visible === false) {
                anyOffscreen = true;
              }
            });
            if (anyOffscreen) {
              const sup = getSuppression(target);
              sup.suppressedUntilVisible = true;
            }
          }
        } catch (e) {}
      };
      target.addEventListener('toggle', handler);
      targetListeners.set(target, handler);
    }
  }

  function scanAndObserve(root = document) {
    const nodes = root.querySelectorAll ? root.querySelectorAll(SELECTOR) : [];
    nodes.forEach(observeElement);
  }

  function init() {
    if (initialized) return;
    initialized = true;
    // Initial scan
    scanAndObserve(document);

    // Watch for new nodes
    const mo = new MutationObserver(mutations => {
      mutations.forEach(m => {
        m.addedNodes.forEach(node => {
          if (node.nodeType !== Node.ELEMENT_NODE) return;
          if (node.matches && node.matches(SELECTOR)) observeElement(node);
          if (node.querySelectorAll) scanAndObserve(node);
        });
      });
    });
    mo.observe(document.documentElement, { childList: true, subtree: true });

    // Re-bind for Theme Editor dynamic loads
    document.addEventListener('shopify:section:load', (e) => {
      if (e && e.target) scanAndObserve(e.target);
    });
    document.addEventListener('shopify:block:load', (e) => {
      if (e && e.target) scanAndObserve(e.target);
    });
  }

  return { init };
})();

// Ensure initialization runs regardless of definition order elsewhere
(function ensureOffscreenObserverInit() {
  if (!window.theme || !window.theme.utils || !window.theme.utils.offscreenPopoverObserver) return;
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      window.theme.utils.offscreenPopoverObserver.init();
    });
  } else {
    window.theme.utils.offscreenPopoverObserver.init();
  }
})();

/**
 * Modal Hover Manager
 * Handles hover-based opening of popovers for modal actions
 * 
 * Usage:
 *   Automatically initializes and manages hover triggers for buttons with
 *   data-modal-trigger="hover" or data-visibility-trigger="hover" attributes
 */
window.theme.utils.modalHoverManager = (function() {
  'use strict';

  class ModalHoverManager {
    constructor() {
      this.hoverTimeouts = new Map();
      this.activePopovers = new Map();
      this.init();
    }

    init() {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.setupHoverTriggers());
      } else {
        this.setupHoverTriggers();
      }

      // Re-initialize when new content is added
      this.observeMutations();
    }

    setupHoverTriggers() {
      // Find all buttons with hover trigger
      // Check for both data-modal-trigger="hover" and data-visibility-trigger="hover"
      const hoverButtons = document.querySelectorAll(
        'button[data-modal-trigger="hover"][popovertarget], ' +
        'button[data-visibility-trigger="hover"][popovertarget], ' +
        'a[data-modal-trigger="hover"][popovertarget], ' +
        'a[data-visibility-trigger="hover"][popovertarget]'
      );

      hoverButtons.forEach(button => {
        if (!this.hoverTimeouts.has(button)) {
          this.registerHoverTrigger(button);
        }
      });
    }

    registerHoverTrigger(button) {
      const popoverId = button.getAttribute('popovertarget');
      if (!popoverId) return;

      const popover = document.getElementById(popoverId);
      if (!popover) {
        // Popover might not be loaded yet, try again later
        setTimeout(() => this.registerHoverTrigger(button), 100);
        return;
      }

      // Check if this is an external-popover component
      const externalPopover = button.closest('external-popover');
      
      let hoverTimeout = null;
      let leaveTimeout = null;

      const handleMouseEnter = async (e) => {
        // Clear any pending leave timeout
        if (leaveTimeout) {
          clearTimeout(leaveTimeout);
          leaveTimeout = null;
        }

        // If external-popover, ensure content is loaded
        if (externalPopover) {
          // Small delay to allow prefetching if needed
          hoverTimeout = setTimeout(async () => {
            try {
              // Wait for external-popover to be fully initialized
              if (typeof externalPopover.connectedCallback === 'undefined') {
                // Component might not be ready, wait a bit more
                await new Promise(resolve => setTimeout(resolve, 50));
              }
              
              // If content isn't loaded yet, fetch it
              if (externalPopover.isLoaded === false && typeof externalPopover.fetchAndInjectContent === 'function') {
                await externalPopover.fetchAndInjectContent();
              }
              
              // Open the popover
              if (typeof externalPopover.openPopover === 'function') {
                externalPopover.openPopover();
              } else if (popover && typeof popover.showPopover === 'function') {
                if (!popover.matches(':popover-open')) {
                  popover.showPopover();
                }
              }
            } catch (error) {
              console.error('ModalHoverManager: Error opening popover on hover', error);
              // Fallback to direct popover control
              if (popover && typeof popover.showPopover === 'function') {
                if (!popover.matches(':popover-open')) {
                  popover.showPopover();
                }
              }
            }
          }, 150); // Small delay for better UX
        } else {
          // Regular popover (no external-popover wrapper)
          hoverTimeout = setTimeout(() => {
            if (popover && typeof popover.showPopover === 'function') {
              if (!popover.matches(':popover-open')) {
                popover.showPopover();
              }
            }
          }, 150);
        }

        this.hoverTimeouts.set(button, hoverTimeout);
      };

      const handleMouseLeave = (e) => {
        // Clear hover timeout
        if (hoverTimeout) {
          clearTimeout(hoverTimeout);
          hoverTimeout = null;
          this.hoverTimeouts.delete(button);
        }

        // Check if mouse is moving to the popover
        const relatedTarget = e.relatedTarget;
        if (relatedTarget) {
          // Check if moving to popover itself (not a close button)
          if (popover.contains(relatedTarget) || popover === relatedTarget) {
            // Check if we're moving to a close button
            const isMovingToCloseButton = relatedTarget.closest && relatedTarget.closest('popover-close');
            if (!isMovingToCloseButton) {
              // Mouse is moving to popover (not a close button), don't close yet
              return;
            }
            // If moving to close button, continue to schedule close
          }
        }

        // Schedule close after delay
        // The timeout will check if mouse is actually over popover/button/close button before closing
        leaveTimeout = setTimeout(() => {
          // Check if popover is still open
          if (!popover || !popover.matches(':popover-open')) {
            leaveTimeout = null;
            return;
          }
          
          // Check if mouse is over button
          const isOverButton = button.matches(':hover') || 
                              (document.activeElement && button.contains(document.activeElement));
          
          // Check if mouse is over popover (including any child elements)
          let isOverPopover = popover.matches(':hover') || 
                              popover.contains(document.activeElement);
          
          // Also check if any element inside popover is currently being hovered
          try {
            const hoveredElement = document.querySelector(':hover');
            if (hoveredElement && popover.contains(hoveredElement)) {
              isOverPopover = true;
            }
          } catch (e) {
            // :hover selector might not be supported in some contexts
          }
          
          // If mouse is over button or popover, don't close
          if (isOverButton || isOverPopover) {
            leaveTimeout = null;
            return;
          }
          
          // Check if hovering over any close button
          const allCloseButtons = popover.querySelectorAll('popover-close');
          const isOverCloseButton = Array.from(allCloseButtons).some(closeBtn => 
            closeBtn.matches(':hover') || 
            (document.activeElement && closeBtn.contains(document.activeElement))
          );

          // Only close if mouse is not over button, popover, or close button
          if (!isOverButton && !isOverPopover && !isOverCloseButton) {
            if (externalPopover && typeof externalPopover.togglePopover === 'function') {
              if (popover.matches(':popover-open')) {
                externalPopover.togglePopover();
              }
            } else if (popover && typeof popover.hidePopover === 'function') {
              if (popover.matches(':popover-open')) {
                popover.hidePopover();
              }
            }
          }
          leaveTimeout = null;
        }, 333);
      };

      // Handle popover mouse events
      const handlePopoverMouseEnter = () => {
        // Clear leave timeout when mouse enters popover
        if (leaveTimeout) {
          clearTimeout(leaveTimeout);
          leaveTimeout = null;
        }
      };

      const handlePopoverMouseLeave = (e) => {
        // Check if mouse is moving to the button or a close button
        const relatedTarget = e.relatedTarget;
        if (relatedTarget) {
          // Check if moving to trigger button
          if (button.contains(relatedTarget) || button === relatedTarget) {
            return;
          }
          
          // Check if moving to a close button (if it's click-triggered, we don't close on hover)
          const closeButton = relatedTarget.closest && relatedTarget.closest('popover-close');
          if (closeButton) {
            const closeTriggerType = closeButton.getAttribute('data-visibility-trigger') || 'click';
            // If it's a hover-triggered close button, it will handle closing
            // If it's click-triggered, we continue with our close logic
            if (closeTriggerType === 'hover') {
              // Don't close here, let the close button handle it
              return;
            }
          }
        }

        // Close popover when mouse leaves (333ms delay as specified)
        leaveTimeout = setTimeout(() => {
          const isOverButton = button.matches(':hover') || button.contains(document.activeElement);
          const isOverPopover = popover.matches(':hover') || popover.contains(document.activeElement);
          
          // Check if hovering over a hover-triggered close button
          const closeButtons = popover.querySelectorAll('popover-close[data-visibility-trigger="hover"]');
          const isOverCloseButton = Array.from(closeButtons).some(closeBtn => 
            closeBtn.matches(':hover') || closeBtn.contains(document.activeElement)
          );

          if (!isOverButton && !isOverPopover && !isOverCloseButton) {
            if (externalPopover && typeof externalPopover.togglePopover === 'function') {
              if (popover.matches(':popover-open')) {
                externalPopover.togglePopover();
              }
            } else if (popover && typeof popover.hidePopover === 'function') {
              if (popover.matches(':popover-open')) {
                popover.hidePopover();
              }
            }
          }
          leaveTimeout = null;
        }, 333);
      };

      // Remove existing listeners if any
      button.removeEventListener('mouseenter', handleMouseEnter);
      button.removeEventListener('mouseleave', handleMouseLeave);
      popover.removeEventListener('mouseenter', handlePopoverMouseEnter);
      popover.removeEventListener('mouseleave', handlePopoverMouseLeave);

      // Add event listeners
      button.addEventListener('mouseenter', handleMouseEnter);
      button.addEventListener('mouseleave', handleMouseLeave);
      popover.addEventListener('mouseenter', handlePopoverMouseEnter);
      popover.addEventListener('mouseleave', handlePopoverMouseLeave);

      // Note: For hover triggers, we keep the popovertarget attribute so that
      // clicks still work as a fallback/toggle. The browser's default toggle behavior
      // works perfectly with our hover behavior:
      // - Hover opens the popover (our script)
      // - Click toggles the popover (browser default) - closes if open from hover
      // - Mouse leave closes it after delay (our script)
      // This provides a good user experience without conflicts.
    }

    observeMutations() {
      const observer = new MutationObserver((mutations) => {
        let shouldReinit = false;

        mutations.forEach((mutation) => {
          if (mutation.type === 'childList') {
            mutation.addedNodes.forEach((node) => {
              if (node.nodeType === Node.ELEMENT_NODE) {
                // Check for new hover trigger buttons
                if (node.hasAttribute && 
                    ((node.hasAttribute('data-modal-trigger') && node.getAttribute('data-modal-trigger') === 'hover') ||
                     (node.hasAttribute('data-visibility-trigger') && node.getAttribute('data-visibility-trigger') === 'hover')) &&
                    node.hasAttribute('popovertarget')) {
                  shouldReinit = true;
                } else if (node.querySelector && 
                           (node.querySelector('[data-modal-trigger="hover"][popovertarget]') ||
                            node.querySelector('[data-visibility-trigger="hover"][popovertarget]'))) {
                  shouldReinit = true;
                }

                // Check for external-popover components being added
                if (node.tagName === 'EXTERNAL-POPOVER' || 
                    (node.querySelector && node.querySelector('external-popover'))) {
                  shouldReinit = true;
                }

                // Check for popover elements being added
                if (node.hasAttribute && node.hasAttribute('popover')) {
                  shouldReinit = true;
                } else if (node.querySelector && node.querySelector('[popover]')) {
                  shouldReinit = true;
                }
              }
            });
          }

          // Also watch for attribute changes on buttons
          if (mutation.type === 'attributes') {
            const target = mutation.target;
            if (target.hasAttribute && 
                ((target.hasAttribute('data-modal-trigger') && target.getAttribute('data-modal-trigger') === 'hover') ||
                 (target.hasAttribute('data-visibility-trigger') && target.getAttribute('data-visibility-trigger') === 'hover')) &&
                target.hasAttribute('popovertarget')) {
              shouldReinit = true;
            }
          }
        });

        if (shouldReinit) {
          // Small delay to ensure DOM is fully updated
          setTimeout(() => {
            this.setupHoverTriggers();
          }, 50);
        }
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['data-modal-trigger', 'data-visibility-trigger', 'popovertarget']
      });
    }

    // Cleanup method
    destroy() {
      this.hoverTimeouts.forEach((timeout) => {
        if (timeout) clearTimeout(timeout);
      });
      this.hoverTimeouts.clear();
      this.activePopovers.clear();
    }
  }

  // Create and return the manager instance
  const manager = new ModalHoverManager();
  return manager;
})();

// Ensure initialization runs regardless of definition order elsewhere
(function ensureModalHoverManagerInit() {
  if (!window.theme || !window.theme.utils || !window.theme.utils.modalHoverManager) return;
  // The manager auto-initializes in its constructor, so we just need to ensure it exists
  // This is mainly for consistency with other utils patterns
})();

/**
 * Centralized function to close modals/popovers
 * Dispatches a 'dialog-popover:close' event that both dialog-popover and external-popover components listen for
 * 
 * @param {Object} options - Close options
 * @param {string} [options.modalId] - ID of specific modal to close
 * @param {boolean} [options.closeAll=false] - If true, close all modals
 * @param {boolean} [options.force=false] - If true, force close even if prevent-close is enabled
 */
window.closeDialogPopover = function(options = {}) {
  const { modalId, closeAll = false, force = false } = options;
  const event = new CustomEvent('dialog-popover:close', {
    detail: { modalId, closeAll, force },
    bubbles: true,
    cancelable: true
  });
  document.dispatchEvent(event);
};

/**
 * Product Card Count Custom Element
 * 
 * Displays the count of product-card elements in #collection-product-grid
 * Updates automatically on load and when collection filters change.
 * 
 * Usage:
 *   <product-card-count></product-card-count>
 * 
 * Displays: (x) where x is the number of product-card elements
 */
if (!customElements.get('product-card-count')) {
  class ProductCardCount extends HTMLElement {
    constructor() {
      super();
      this.updateCount = this.updateCount.bind(this);
      this.handleSectionUpdate = this.handleSectionUpdate.bind(this);
    }

    connectedCallback() {
      // Update count on initial load
      this.updateCount();
      
      // Listen for section updates (when filters change)
      document.addEventListener('section-updated', this.handleSectionUpdate);
      
      // Also listen for DOM mutations in case products are added/removed dynamically
      this.observeProductGrid();
    }

    disconnectedCallback() {
      document.removeEventListener('section-updated', this.handleSectionUpdate);
      if (this.mutationObserver) {
        this.mutationObserver.disconnect();
      }
    }

    handleSectionUpdate(event) {
      // Only update if it's a collection-filters update
      if (event.detail && event.detail.type === 'collection-filters') {
        // Wait a bit for DOM to update after section replacement
        setTimeout(() => {
          this.updateCount();
        }, 100);
      }
    }

    observeProductGrid() {
      const grid = document.getElementById('collection-product-grid');
      if (!grid) return;

      // Use MutationObserver to watch for product-card changes
      this.mutationObserver = new MutationObserver(() => {
        this.updateCount();
      });

      this.mutationObserver.observe(grid, {
        childList: true,
        subtree: true
      });
    }

    updateCount() {
      const grid = document.getElementById('collection-product-grid');
      if (!grid) {
        this.textContent = '(0)';
        return;
      }

      const productCards = grid.querySelectorAll('product-card');
      const count = productCards.length;
      this.textContent = `(${count})`;
    }
  }

  customElements.define('product-card-count', ProductCardCount);
}

/**
 * Popover Trigger Handler
 * 
 * Handles data-popover-target attributes on buttons/elements to trigger
 * popover/popover-element visibility. Works in both modern and fallback modes.
 * Supports both click and hover triggers via data-visibility-trigger attribute.
 * 
 * Usage:
 *   <button data-popover-target="modal-id">Open Modal</button>
 *   <popover-element id="modal-id">...</popover-element>
 * 
 *   <button data-popover-target="modal-id" data-visibility-trigger="hover">Hover Modal</button>
 */
window.theme.utils.popoverTriggerHandler = (function() {
  function showPopover(target) {
    if (!target) return;
    
    // Check if target has showPopover method (both native popover and popover-element)
    if (typeof target.showPopover === 'function') {
      target.showPopover();
    } else if (typeof target.click === 'function' && target.hasAttribute('popovertarget')) {
      // Fallback for elements that might need a click
      target.click();
    } else {
      console.warn(`Popover trigger: Target element does not have showPopover method`, target);
    }
  }

  function handlePopoverTrigger(event) {
    const trigger = event.currentTarget;
    const targetId = trigger.getAttribute('data-popover-target');
    
    if (!targetId) return;
    
    const target = document.getElementById(targetId);
    if (!target) {
      console.warn(`Popover trigger: Target element with id "${targetId}" not found`);
      return;
    }
    
    showPopover(target);
  }
  
  function setupHoverTrigger(trigger) {
    const targetId = trigger.getAttribute('data-popover-target');
    if (!targetId) return;
    
    const target = document.getElementById(targetId);
    if (!target) return;
    
    let hoverTimeout = null;
    const HOVER_DELAY = 150; // Small delay before showing on hover
    
    // Show on hover
    const handleMouseEnter = () => {
      hoverTimeout = setTimeout(() => {
        showPopover(target);
      }, HOVER_DELAY);
    };
    
    // Setup hover listeners
    trigger.addEventListener('mouseenter', handleMouseEnter);
    
    // Store reference for cleanup if needed
    if (!trigger._popoverHoverHandlers) {
      trigger._popoverHoverHandlers = { mouseEnter: handleMouseEnter, timeout: null };
    } else {
      trigger._popoverHoverHandlers.mouseEnter = handleMouseEnter;
    }
  }
  
  function init() {
    // Set up click event delegation for data-popover-target elements without hover trigger
    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-popover-target]');
      if (trigger) {
        const triggerType = trigger.getAttribute('data-visibility-trigger');
        // Only handle click if it's not explicitly a hover trigger
        if (triggerType !== 'hover') {
          handlePopoverTrigger({ currentTarget: trigger });
        }
      }
    }, false);
    
    // Set up hover triggers on existing and new elements
    const setupAllHoverTriggers = () => {
      document.querySelectorAll('[data-popover-target][data-visibility-trigger="hover"]').forEach(trigger => {
        // Skip if already set up
        if (!trigger._popoverHoverSetup) {
          setupHoverTrigger(trigger);
          trigger._popoverHoverSetup = true;
        }
      });
    };
    
    // Initial setup
    setupAllHoverTriggers();
    
    // Watch for dynamically added hover triggers
    const observer = new MutationObserver(() => {
      setupAllHoverTriggers();
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-popover-target', 'data-visibility-trigger']
    });
  }
  
  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  
  return { handlePopoverTrigger, showPopover, init };
})();

/**
 * Hide After Delay Custom Element
 * 
 * Automatically removes the element from the DOM after a specified delay,
 * but only starts the countdown after the element becomes visible in the viewport.
 * 
 * Usage:
 *   <hide-after-delay delay="5000">
 *     <!-- content to hide after 5 seconds (once visible) -->
 *   </hide-after-delay>
 * 
 * @attribute {string} delay - Delay in milliseconds before removing the element (default: 5000)
 */
if (!customElements.get('hide-after-delay')) {
  class HideAfterDelay extends HTMLElement {
    constructor() {
      super();
      this.timeoutId = null;
      this.observer = null;
      this.hasStarted = false;
    }

    connectedCallback() {
      // Only observe if we haven't started yet
      if (this.hasStarted) return;

      // Create a one-time IntersectionObserver to detect when element becomes visible
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !this.hasStarted) {
            this.hasStarted = true;
            
            // Disconnect observer - we only need it once
            if (this.observer) {
              this.observer.disconnect();
              this.observer = null;
            }

            // Get delay from attribute, default to 5000ms (5 seconds)
            const delay = parseInt(this.getAttribute('delay'), 10);
            const delayMs = isNaN(delay) || delay <= 0 ? 5000 : delay;

            // Set timeout to remove element after delay
            this.timeoutId = setTimeout(() => {
              this.remove();
            }, delayMs);
          }
        });
      }, {
        threshold: 0.01 // Trigger when at least 1% of element is visible
      });

      // Start observing this element
      this.observer.observe(this);
    }

    disconnectedCallback() {
      // Clean up observer
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }

      // Clean up timeout if element is removed before delay completes
      if (this.timeoutId) {
        clearTimeout(this.timeoutId);
        this.timeoutId = null;
      }
    }
  }

  customElements.define('hide-after-delay', HideAfterDelay);
}

/**
 * Breadcrumb Nav Custom Element
 *
 * On product pages, enhances the collection breadcrumb link to use the referrer URL
 * when the user came from the same collection (e.g. with filters), so "back"
 * navigates to the filtered collection.
 *
 * Usage:
 *   <breadcrumb-nav data-enhance-referrer="true" aria-label="Breadcrumb" ...>
 *     <a href="/collections/...">Collection</a> / <span>Product</span>
 *   </breadcrumb-nav>
 *
 * Set data-enhance-referrer="true" only on product pages.
 */
if (!customElements.get('breadcrumb-nav')) {
  class BreadcrumbNav extends HTMLElement {
    connectedCallback() {
      if (this.getAttribute('data-enhance-referrer') !== 'true') return;
      if (!document.referrer) return;

      const link = this.querySelector('a[href*="/collections/"]');
      if (!link) return;

      try {
        const referrerUrl = new URL(document.referrer);
        const linkUrl = new URL(link.href);
        if (referrerUrl.origin !== window.location.origin) return;
        if (referrerUrl.pathname !== linkUrl.pathname) return;
        link.href = document.referrer;
      } catch (e) {}
    }
  }

  customElements.define('breadcrumb-nav', BreadcrumbNav);
}
